/**
 * /parity/uo_uoc — 线网同构：`admin/uo.uoc/index`（只读池 + 表单，非 `php_user_user` 列表；spec 曾误为 list）
 * 首屏与静态 HTML 一致；线网 uoc.js 另挂用户大表。见 docs/uoclub262728-admin-auth-crawl-full.md
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, ConfigProvider, Form, Input, Modal, Select, theme } from 'antd';
import type { CSSProperties } from 'react';
import { useAuthStore } from '../../store/auth';
import { api, ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { TOKEN_META } from '@uo/shared';

type LegacySnap = {
  uoc: string;
  rp: string;
  tp: string;
  ep: string;
  mirror_imported_at: string | null;
  has_mirror: boolean;
};

type LogRow = {
  id: string;
  user_id: number;
  asset: string;
  delta: string;
  note: string | null;
  created_at: string;
};

/** 与线网 readonly layui-input 灰底 #9F9F9F 一致 */
const poolInputStyle: CSSProperties = {
  marginLeft: 8,
  flex: 1,
  maxWidth: 560,
  backgroundColor: '#9F9F9F',
  color: '#fff',
  fontWeight: 500,
  border: '1px solid #8c8c8c',
};

const readRowBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  marginBottom: 12,
  fontSize: 14,
};

const formItemMb = 18;

export function ParityUoUocPage() {
  const { message: msg } = App.useApp();
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const hasMenu = useAuthStore((s) => s.hasMenu);
  const isSuper = useAuthStore((s) => s.profile?.is_super === true);
  const canUse =
    isSuper ||
    hasMenu('parity.uo_uoc') ||
    hasNode('parity.uo_uoc') ||
    hasNode('platform.user.transfer_coin') ||
    hasMenu('platform.transfer_coin');

  const { data: snap, isLoading, refetch: refetchSnap } = useQuery({
    queryKey: ['parity-uo-uoc', 'legacy-php-form'],
    queryFn: () => api.get<LegacySnap>('/api/admin/parity-uo-uoc/legacy-php-form'),
    enabled: canUse,
  });

  const [form] = Form.useForm<{ account: string; num: string; coin: 'rp' | 'tp' | 'ep' }>();
  const { token } = theme.useToken();

  const mut = useMutation({
    mutationFn: (v: { account: string; num: string; coin: 'rp' | 'tp' | 'ep'; xian: boolean }) =>
      api.post<unknown>('/api/admin/parity-uo-uoc/distribute', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-transfer-coin-log'] });
      void refetchSnap();
      msg.success('发放成功');
      form.resetFields(['num', 'account']);
    },
    onError: (e) => msg.error(e instanceof ApiError ? e.message : '失败'),
  });

  const onSubmit = () => {
    form
      .validateFields()
      .then((v) => {
        Modal.confirm({
          title: '确定操作吗？',
          onOk: () => {
            mut.mutate({
                account: v.account,
                num: String(v.num).trim(),
                coin: v.coin,
                xian: false,
              });
          },
        });
      })
      .catch(() => {});
  };

  const logColumns = [
    { title: '金额', dataIndex: 'delta', width: 120 },
    { title: '备注', dataIndex: 'note', width: 300, ellipsis: true },
    {
      title: '资产',
      dataIndex: 'asset',
      width: 72,
      render: (v: string) => (TOKEN_META as Record<string, { symbol: string }>)[v]?.symbol ?? v,
    },
    {
      title: '发放时间',
      dataIndex: 'created_at',
      width: 180,
      render: (t: string) => new Date(t).toLocaleString('zh-CN'),
    },
  ];

  if (!canUse) {
    return <p style={{ color: '#999' }}>无权限：需要 parity.uo_uoc 或 platform.user.transfer_coin</p>;
  }

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: '#1677ff' },
      }}
    >
      <div className="parity-uo-uoc-page" style={{ background: '#fff', minHeight: 360, padding: '0 0 8px' }}>
        <div style={{ maxWidth: 900 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: 'rgba(0,0,0,0.88)',
              marginBottom: 14,
            }}
          >
            UOC转TP/EP/RP
          </div>
          <p style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 12, lineHeight: 1.6 }}>
            与线网 Layui 一致首屏为 UOC/RP/TP/EP 只读 +「发放数量」「转换币种」+ 发放。线网 JS
            另外在回调里挂「用户表」多选后提交 <code>uo.uoc/activation_to</code>，本系统未复刻同一
            张表时，用下方「用户登录名」显式指定收款用户（同一条 ledger 转币能力）。
          </p>
          {snap && !snap.has_mirror && !isLoading && (
            <div
              style={{
                marginBottom: 12,
                padding: 10,
                background: token.colorInfoBg,
                border: `1px solid ${token.colorBorderSecondary}`,
                fontSize: 12,
              }}
            >
              无 php_uo_uo_config 镜像时池子显示为 0。导入镜像后刷新页面可同步与线网一致的只读数。
            </div>
          )}
          {isLoading ? null : (
            <>
              <div style={readRowBase}>
                <span style={{ width: 200, textAlign: 'right', color: 'rgba(0,0,0,0.85)' }}>UOC剩余</span>
                <Input
                  readOnly
                  value={snap?.uoc ?? ''}
                  size="large"
                  style={poolInputStyle}
                />
              </div>
              <div style={readRowBase}>
                <span style={{ width: 200, textAlign: 'right' }}>RP</span>
                <Input readOnly value={snap?.rp ?? ''} size="large" style={poolInputStyle} />
              </div>
              <div style={readRowBase}>
                <span style={{ width: 200, textAlign: 'right' }}>TP</span>
                <Input readOnly value={snap?.tp ?? ''} size="large" style={poolInputStyle} />
              </div>
              <div style={readRowBase}>
                <span style={{ width: 200, textAlign: 'right' }}>EP</span>
                <Input readOnly value={snap?.ep ?? ''} size="large" style={poolInputStyle} />
              </div>
            </>
          )}
          <Form
            form={form}
            layout="horizontal"
            labelAlign="right"
            colon={false}
            requiredMark
            style={{ maxWidth: 900 }}
            initialValues={{ coin: 'rp' as const }}
            labelCol={{ flex: '0 0 200px' }}
            wrapperCol={{ flex: '1 1 auto' }}
          >
            <Form.Item
              name="num"
              label="发放数量"
              rules={[{ required: true, message: '请输入发放数量' }]}
              style={{ marginBottom: formItemMb }}
            >
              <Input size="large" placeholder="发放数量" allowClear style={{ maxWidth: '100%' }} />
            </Form.Item>
            <Form.Item
              name="coin"
              label="转换币种"
              rules={[{ required: true, message: '请选择转换币种' }]}
              style={{ marginBottom: formItemMb }}
            >
              <Select
                size="large"
                style={{ maxWidth: '100%' }}
                placeholder="请选择"
                options={[
                  { value: 'rp' as const, label: 'RP' },
                  { value: 'tp' as const, label: 'TP' },
                  { value: 'ep' as const, label: 'EP' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="account"
              label="用户登录名"
              rules={[{ required: true, message: '请输入登录名' }]}
              style={{ marginBottom: 22 }}
            >
              <Input size="large" placeholder="登录名" autoComplete="off" style={{ maxWidth: '100%' }} />
            </Form.Item>
            <div style={{ position: 'relative', minHeight: 44, marginTop: 12 }}>
              <Button
                type="primary"
                size="small"
                style={{ position: 'absolute', left: 0, bottom: 0 }}
                onClick={() => {
                  void refetchSnap();
                  void qc.invalidateQueries({ queryKey: ['admin-transfer-coin-log'] });
                  msg.info('已刷新数据');
                }}
              >
                刷新页面
              </Button>
              <div style={{ display: 'flex', justifyContent: 'center', paddingLeft: 100 }}>
                <Button
                  type="primary"
                  size="large"
                  loading={mut.isPending}
                  onClick={onSubmit}
                  style={{ minWidth: 140, height: 40 }}
                >
                  发放
                </Button>
              </div>
            </div>
          </Form>
        </div>

        <div
          style={{
            marginTop: 28,
            paddingTop: 20,
            borderTop: '1px solid #e8e8e8',
            maxWidth: 900,
          }}
        >
          <div style={{ marginBottom: 12, fontSize: 15, fontWeight: 600, color: 'rgba(0,0,0,0.88)' }}>转币记录</div>
          <DataTable<LogRow>
            queryKey={['admin-transfer-coin-log', 'parity-uoc']}
            endpoint="/api/admin/transfer-coin/log"
            columns={logColumns}
            rowKey="id"
          />
        </div>
      </div>
    </ConfigProvider>
  );
}
